/**
 * Certificados — emision de certificados clinicos (aptitud visual, examen visual,
 * medico oftalmologico, constancia) con auto-relleno desde la historia clinica,
 * edicion libre, PDF con membrete/logo, descarga y envio por correo.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import { certificadosAPI, clientesAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

const TIPOS = [
  { value: 'aptitud_visual', label: 'Aptitud Visual', icon: 'bi-car-front-fill', desc: 'Licencia de conducir, trabajo o estudios' },
  { value: 'examen_visual', label: 'Examen Visual', icon: 'bi-eyeglasses', desc: 'Agudeza visual y refracción (OD/OI)' },
  { value: 'medico_oftalmologico', label: 'Médico Oftalmológico', icon: 'bi-clipboard2-pulse-fill', desc: 'Diagnóstico / salud ocular' },
  { value: 'constancia', label: 'Constancia de Atención', icon: 'bi-file-earmark-text-fill', desc: 'Comprobante simple de asistencia' }
];
const TIPO_LABEL = TIPOS.reduce((a, t) => { a[t.value] = t.label; return a; }, {});

const FORM_INI = {
  titulo: '', cuerpo: '', diagnostico: '', observaciones: '',
  profesional_nombre: '', profesional_registro: '', lugar_emision: '',
  fecha_emision: '', vigencia_hasta: '', email_destinatario: '',
  agudeza: { od_sc: '', oi_sc: '', od_cc: '', oi_cc: '' },
  refraccion: { od: { esf: '', cil: '', eje: '', add: '' }, oi: { esf: '', cil: '', eje: '', add: '' } },
  requiere_lentes: false
};

const hoyISO = () => new Date().toISOString().slice(0, 10);

// Variables disponibles en el texto de las plantillas (clic para insertar)
const VARIABLES_UI = [
  ['paciente', 'Nombre del paciente'], ['cedula', 'Cédula'], ['tipo_id', 'Tipo de identificación'],
  ['edad', 'Edad'], ['sexo', 'Sexo'], ['fecha', 'Fecha de emisión'],
  ['od_lejos', 'AV OD lejos'], ['oi_lejos', 'AV OI lejos'],
  ['od_sc', 'AV OD sin corr.'], ['oi_sc', 'AV OI sin corr.'], ['od_cc', 'AV OD con corr.'], ['oi_cc', 'AV OI con corr.'],
  ['correccion', 'Frase de uso de lentes'], ['diagnostico', 'Diagnóstico'], ['optometrista', 'Profesional'], ['optica', 'Óptica']
];

const Certificados = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ total: 0, page: 1, totalPages: 1 });
  const [filtros, setFiltros] = useState({ buscar: '', tipo: '', page: 1 });

  // Wizard de creacion
  const [showNuevo, setShowNuevo] = useState(false);
  const [tipo, setTipo] = useState('');
  const [paciente, setPaciente] = useState(null);
  const [hcId, setHcId] = useState(null);
  const [busqPac, setBusqPac] = useState('');
  const [resultadosPac, setResultadosPac] = useState([]);
  const [cargandoBorrador, setCargandoBorrador] = useState(false);
  const [form, setForm] = useState(FORM_INI);
  const [emitiendo, setEmitiendo] = useState(false);

  // Anular
  const [showAnular, setShowAnular] = useState(null);
  const [motivoAnular, setMotivoAnular] = useState('');

  // Plantillas (editar = admin/gerente)
  const { usuario } = useAuth();
  const puedeEditarPlantillas = ['admin', 'super_admin', 'gerente'].includes(usuario?.rol) || (usuario?.permisos || []).includes('*');
  const [showPlantillas, setShowPlantillas] = useState(false);
  const [plantillas, setPlantillas] = useState([]);
  const [emisor, setEmisor] = useState({ registro_profesional: '', lugar_emision: '' });
  const [tipoPlantilla, setTipoPlantilla] = useState('aptitud_visual');
  const [guardandoPlantilla, setGuardandoPlantilla] = useState(false);
  const cuerpoRef = useRef(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const params = { ...filtros, limit: 20 };
      Object.keys(params).forEach(k => (params[k] === '' || params[k] === null) && delete params[k]);
      const res = await certificadosAPI.getAll(params);
      if (res.data.success) {
        setItems(res.data.data);
        setPagination(res.data.pagination || { total: res.data.data.length, page: 1, totalPages: 1 });
      }
    } catch (e) {
      toast.error('Error al cargar certificados');
    } finally {
      setLoading(false);
    }
  }, [filtros]);

  useEffect(() => { load(); }, [load]);

  // Busqueda de paciente con debounce
  useEffect(() => {
    if (!showNuevo || paciente) return;
    const q = busqPac.trim();
    if (q.length < 2) { setResultadosPac([]); return; }
    const t = setTimeout(async () => {
      try {
        const res = await clientesAPI.getAll({ buscar: q, limit: 8 });
        if (res.data.success) setResultadosPac(res.data.data);
      } catch (e) { /* silencioso */ }
    }, 350);
    return () => clearTimeout(t);
  }, [busqPac, showNuevo, paciente]);

  const abrirNuevo = () => {
    setTipo(''); setPaciente(null); setHcId(null);
    setBusqPac(''); setResultadosPac([]); setForm(FORM_INI);
    setShowNuevo(true);
  };

  const cerrarNuevo = () => { setShowNuevo(false); };

  const elegirPaciente = async (p) => {
    setPaciente(p);
    setCargandoBorrador(true);
    try {
      const res = await certificadosAPI.getBorrador({ cliente_id: p.id, tipo });
      if (res.data.success) {
        const b = res.data.data;
        setHcId(b.historia_clinica_id || null);
        const vigHasta = b.vigencia_meses
          ? (() => { const d = new Date(); d.setMonth(d.getMonth() + b.vigencia_meses); return d.toISOString().slice(0, 10); })()
          : '';
        setForm({
          ...FORM_INI,
          titulo: b.titulo || '',
          cuerpo: b.cuerpo || '',
          diagnostico: b.diagnostico || '',
          observaciones: b.observaciones || '',
          profesional_nombre: b.profesional_nombre || '',
          profesional_registro: b.profesional_registro || '',
          lugar_emision: b.lugar_emision || '',
          fecha_emision: hoyISO(),
          vigencia_hasta: vigHasta,
          email_destinatario: b.paciente?.email || '',
          agudeza: { ...FORM_INI.agudeza, ...(b.datos_json?.agudeza || {}) },
          refraccion: {
            od: { ...FORM_INI.refraccion.od, ...(b.datos_json?.refraccion?.od || {}) },
            oi: { ...FORM_INI.refraccion.oi, ...(b.datos_json?.refraccion?.oi || {}) }
          },
          requiere_lentes: b.datos_json?.aptitud?.requiere_lentes || false
        });
      }
    } catch (e) {
      toast.error(e.response?.data?.error || 'Error al cargar los datos del paciente');
    } finally {
      setCargandoBorrador(false);
    }
  };

  const cambiarPaciente = () => { setPaciente(null); setHcId(null); setBusqPac(''); setResultadosPac([]); };

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setAgudeza = (k, v) => setForm(f => ({ ...f, agudeza: { ...f.agudeza, [k]: v } }));
  const setRefr = (ojo, k, v) => setForm(f => ({ ...f, refraccion: { ...f.refraccion, [ojo]: { ...f.refraccion[ojo], [k]: v } } }));

  const emitir = async () => {
    if (!paciente || !tipo) return;
    if (!form.cuerpo || !form.cuerpo.trim()) { toast.error('El cuerpo del certificado no puede estar vacío'); return; }
    setEmitiendo(true);
    try {
      const datos_json = {
        agudeza: form.agudeza,
        refraccion: form.refraccion,
        ...(tipo === 'aptitud_visual' ? { aptitud: { requiere_lentes: form.requiere_lentes } } : {})
      };
      const payload = {
        cliente_id: paciente.id,
        tipo,
        historia_clinica_id: hcId,
        titulo: form.titulo,
        cuerpo: form.cuerpo,
        datos_json,
        diagnostico: form.diagnostico || null,
        observaciones: form.observaciones || null,
        profesional_nombre: form.profesional_nombre || null,
        profesional_registro: form.profesional_registro || null,
        lugar_emision: form.lugar_emision || null,
        fecha_emision: form.fecha_emision || null,
        vigencia_hasta: form.vigencia_hasta || null,
        email_destinatario: form.email_destinatario || null
      };
      const res = await certificadosAPI.create(payload);
      if (res.data.success) {
        const nuevo = res.data.data;
        toast.success(`Certificado ${nuevo.codigo} emitido`);
        setShowNuevo(false);
        load();
        // abrir el PDF recien emitido
        descargarPDF(nuevo.id);
      }
    } catch (e) {
      toast.error(e.response?.data?.error || 'Error al emitir el certificado');
    } finally {
      setEmitiendo(false);
    }
  };

  const descargarPDF = async (id) => {
    try {
      const res = await certificadosAPI.descargarPDF(id);
      const url = URL.createObjectURL(res.data);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error('Error al abrir el PDF');
    }
  };

  const enviarEmailRow = async (cert) => {
    const email = window.prompt('Correo del destinatario:', cert.email_destinatario || '');
    if (!email) return;
    try {
      await certificadosAPI.enviarEmail(cert.id, { destinatario: email });
      toast.success(`Certificado enviado a ${email}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Error al enviar el correo');
    }
  };

  const confirmarAnular = async () => {
    if (!motivoAnular || motivoAnular.trim().length < 5) { toast.error('Indica un motivo (mínimo 5 caracteres)'); return; }
    try {
      await certificadosAPI.anular(showAnular, { motivo: motivoAnular.trim() });
      toast.success('Certificado anulado');
      setShowAnular(null); setMotivoAnular('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Error al anular');
    }
  };

  // ── Plantillas ──
  const abrirPlantillas = async () => {
    setShowPlantillas(true);
    try {
      const res = await certificadosAPI.getPlantillas();
      if (res.data.success) {
        setPlantillas(res.data.data || []);
        setEmisor(res.data.emisor || { registro_profesional: '', lugar_emision: '' });
        setTipoPlantilla((res.data.data && res.data.data[0]?.tipo) || 'aptitud_visual');
      }
    } catch (e) {
      toast.error('Error al cargar las plantillas');
    }
  };

  const plantillaActual = plantillas.find(p => p.tipo === tipoPlantilla);
  const setPlantillaCampo = (campo, valor) =>
    setPlantillas(arr => arr.map(p => (p.tipo === tipoPlantilla ? { ...p, [campo]: valor } : p)));

  const insertarVariable = (k) => {
    const ta = cuerpoRef.current;
    const texto = plantillaActual?.cuerpo || '';
    if (!ta) { setPlantillaCampo('cuerpo', texto + `{{${k}}}`); return; }
    const s = ta.selectionStart ?? texto.length;
    const e = ta.selectionEnd ?? texto.length;
    setPlantillaCampo('cuerpo', texto.slice(0, s) + `{{${k}}}` + texto.slice(e));
    setTimeout(() => { ta.focus(); const pos = s + k.length + 4; ta.setSelectionRange(pos, pos); }, 0);
  };

  const guardarPlantillas = async () => {
    setGuardandoPlantilla(true);
    try {
      await Promise.all(plantillas.map(p => certificadosAPI.updatePlantilla(p.tipo, {
        titulo: p.titulo, cuerpo: p.cuerpo, observaciones: p.observaciones, vigencia_meses: p.vigencia_meses
      })));
      await certificadosAPI.updateConfigEmisor(emisor);
      toast.success('Plantillas guardadas. Los próximos certificados saldrán con estos textos.');
      setShowPlantillas(false);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Error al guardar (¿requiere permiso de admin/gerente?)');
    } finally {
      setGuardandoPlantilla(false);
    }
  };

  const fmtFecha = (f) => {
    if (!f) return '—';
    const d = new Date(f);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('es-EC');
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-start mb-4 flex-wrap gap-3">
        <div>
          <h1 className="h3 mb-1"><i className="bi bi-file-earmark-medical-fill me-2 text-primary"></i>Certificados</h1>
          <p className="text-muted mb-0">Emite certificados médicos y de examen visual con el logo de la óptica.</p>
        </div>
        <div className="d-flex gap-2">
          {puedeEditarPlantillas && (
            <button className="btn btn-outline-secondary" onClick={abrirPlantillas} title="Editar el texto base de cada tipo">
              <i className="bi bi-pencil-square me-1"></i> Plantillas
            </button>
          )}
          <button className="btn btn-primary" onClick={abrirNuevo}>
            <i className="bi bi-plus-lg me-1"></i> Nuevo Certificado
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-7">
              <label className="form-label small text-muted">Buscar</label>
              <input type="text" className="form-control" placeholder="Código, nombre o cédula del paciente"
                value={filtros.buscar}
                onChange={e => setFiltros(f => ({ ...f, buscar: e.target.value, page: 1 }))} />
            </div>
            <div className="col-md-5">
              <label className="form-label small text-muted">Tipo</label>
              <select className="form-select" value={filtros.tipo}
                onChange={e => setFiltros(f => ({ ...f, tipo: e.target.value, page: 1 }))}>
                <option value="">Todos</option>
                {TIPOS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Tabla */}
      <div className="card border-0 shadow-sm">
        <div className="table-responsive">
          <table className="table table-hover align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th>Código</th><th>Tipo</th><th>Paciente</th><th>Fecha</th>
                <th className="text-center">Estado</th><th className="text-end">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="6" className="text-center py-5"><div className="spinner-border text-primary" role="status"></div></td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan="6" className="text-center text-muted py-5">
                  <i className="bi bi-file-earmark-medical d-block mb-2" style={{ fontSize: '2rem' }}></i>
                  No hay certificados todavía. Crea el primero con "Nuevo Certificado".
                </td></tr>
              ) : items.map(c => (
                <tr key={c.id}>
                  <td><span className="fw-semibold">{c.codigo}</span></td>
                  <td><span className="badge bg-light text-dark border">{TIPO_LABEL[c.tipo] || c.tipo}</span></td>
                  <td>
                    <div className="fw-semibold">{`${c.nombres || ''} ${c.apellidos || ''}`.trim() || '—'}</div>
                    <small className="text-muted">{c.identificacion}</small>
                  </td>
                  <td>{fmtFecha(c.fecha_emision)}</td>
                  <td className="text-center">
                    {c.estado === 'anulado'
                      ? <span className="badge bg-danger">Anulado</span>
                      : <span className="badge bg-success">Emitido</span>}
                    {c.enviado_email && <i className="bi bi-envelope-check-fill text-success ms-2" title="Enviado por correo"></i>}
                  </td>
                  <td className="text-end">
                    <button className="btn btn-sm btn-outline-primary me-1" title="Ver / Descargar PDF" onClick={() => descargarPDF(c.id)}>
                      <i className="bi bi-file-earmark-pdf"></i>
                    </button>
                    <button className="btn btn-sm btn-outline-secondary me-1" title="Enviar por correo" onClick={() => enviarEmailRow(c)}>
                      <i className="bi bi-envelope"></i>
                    </button>
                    {c.estado !== 'anulado' && (
                      <button className="btn btn-sm btn-outline-danger" title="Anular" onClick={() => { setShowAnular(c.id); setMotivoAnular(''); }}>
                        <i className="bi bi-x-circle"></i>
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {pagination.totalPages > 1 && (
          <div className="card-footer d-flex justify-content-between align-items-center">
            <small className="text-muted">{pagination.total} certificados</small>
            <div className="btn-group">
              <button className="btn btn-sm btn-outline-secondary" disabled={filtros.page <= 1}
                onClick={() => setFiltros(f => ({ ...f, page: f.page - 1 }))}>Anterior</button>
              <span className="btn btn-sm btn-light disabled">{pagination.page} / {pagination.totalPages}</span>
              <button className="btn btn-sm btn-outline-secondary" disabled={filtros.page >= pagination.totalPages}
                onClick={() => setFiltros(f => ({ ...f, page: f.page + 1 }))}>Siguiente</button>
            </div>
          </div>
        )}
      </div>

      {/* ─── MODAL NUEVO CERTIFICADO ─── */}
      {showNuevo && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-xl modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title"><i className="bi bi-file-earmark-medical-fill me-2"></i>Nuevo Certificado</h5>
                <button type="button" className="btn-close" onClick={cerrarNuevo}></button>
              </div>
              <div className="modal-body">

                {/* Paso 1: tipo */}
                {!tipo && (
                  <>
                    <p className="text-muted">Selecciona el tipo de certificado:</p>
                    <div className="row g-3">
                      {TIPOS.map(t => (
                        <div className="col-md-6" key={t.value}>
                          <button className="btn btn-outline-primary w-100 text-start p-3 h-100" onClick={() => setTipo(t.value)}>
                            <i className={`bi ${t.icon} fs-4 me-2`}></i>
                            <span className="fw-semibold">{t.label}</span>
                            <div className="small text-muted mt-1">{t.desc}</div>
                          </button>
                        </div>
                      ))}
                    </div>
                  </>
                )}

                {/* Paso 2: paciente */}
                {tipo && !paciente && (
                  <>
                    <div className="d-flex align-items-center mb-3">
                      <span className="badge bg-primary me-2">{TIPO_LABEL[tipo]}</span>
                      <button className="btn btn-sm btn-link text-decoration-none p-0" onClick={() => setTipo('')}>
                        <i className="bi bi-arrow-left"></i> cambiar tipo
                      </button>
                    </div>
                    <label className="form-label">Buscar paciente (por cédula, nombre o código)</label>
                    <input type="text" className="form-control" autoFocus placeholder="Escribe al menos 2 caracteres…"
                      value={busqPac} onChange={e => setBusqPac(e.target.value)} />
                    <div className="list-group mt-2">
                      {resultadosPac.map(p => (
                        <button key={p.id} className="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                          onClick={() => elegirPaciente(p)}>
                          <span>
                            <span className="fw-semibold">{`${p.nombres || ''} ${p.apellidos || ''}`.trim()}</span>
                            <small className="text-muted ms-2">{p.identificacion}</small>
                          </span>
                          <small className="text-muted">{p.codigo}</small>
                        </button>
                      ))}
                      {busqPac.trim().length >= 2 && resultadosPac.length === 0 && (
                        <div className="text-muted small px-1 py-2">Sin coincidencias.</div>
                      )}
                    </div>
                  </>
                )}

                {/* Paso 3: formulario editable */}
                {tipo && paciente && (
                  cargandoBorrador ? (
                    <div className="text-center py-5"><div className="spinner-border text-primary"></div><p className="text-muted mt-2">Cargando datos del paciente…</p></div>
                  ) : (
                    <>
                      <div className="d-flex flex-wrap justify-content-between align-items-center bg-light rounded p-2 mb-3">
                        <div>
                          <span className="badge bg-primary me-2">{TIPO_LABEL[tipo]}</span>
                          <span className="fw-semibold">{`${paciente.nombres || ''} ${paciente.apellidos || ''}`.trim()}</span>
                          <small className="text-muted ms-2">{paciente.identificacion}</small>
                        </div>
                        <button className="btn btn-sm btn-outline-secondary" onClick={cambiarPaciente}>
                          <i className="bi bi-arrow-repeat"></i> Cambiar paciente
                        </button>
                      </div>

                      <div className="mb-3">
                        <label className="form-label">Título</label>
                        <input type="text" className="form-control" value={form.titulo} onChange={e => setField('titulo', e.target.value)} />
                      </div>

                      <div className="mb-3">
                        <label className="form-label">Cuerpo del certificado <small className="text-muted">(editable)</small></label>
                        <textarea className="form-control" rows={8} value={form.cuerpo} onChange={e => setField('cuerpo', e.target.value)} />
                      </div>

                      {/* Agudeza visual (examen / aptitud) */}
                      {(tipo === 'examen_visual' || tipo === 'aptitud_visual') && (
                        <div className="border rounded p-3 mb-3">
                          <h6 className="mb-3"><i className="bi bi-eye me-2"></i>Agudeza visual (lejos)</h6>
                          <div className="row g-2">
                            <div className="col-6 col-md-3"><label className="form-label small">OD sin corrección</label><input className="form-control form-control-sm" value={form.agudeza.od_sc} onChange={e => setAgudeza('od_sc', e.target.value)} /></div>
                            <div className="col-6 col-md-3"><label className="form-label small">OI sin corrección</label><input className="form-control form-control-sm" value={form.agudeza.oi_sc} onChange={e => setAgudeza('oi_sc', e.target.value)} /></div>
                            <div className="col-6 col-md-3"><label className="form-label small">OD con corrección</label><input className="form-control form-control-sm" value={form.agudeza.od_cc} onChange={e => setAgudeza('od_cc', e.target.value)} /></div>
                            <div className="col-6 col-md-3"><label className="form-label small">OI con corrección</label><input className="form-control form-control-sm" value={form.agudeza.oi_cc} onChange={e => setAgudeza('oi_cc', e.target.value)} /></div>
                          </div>
                        </div>
                      )}

                      {/* Refraccion (examen visual) */}
                      {tipo === 'examen_visual' && (
                        <div className="border rounded p-3 mb-3">
                          <h6 className="mb-3"><i className="bi bi-rulers me-2"></i>Refracción (Rx final)</h6>
                          {['od', 'oi'].map(ojo => (
                            <div className="row g-2 mb-2 align-items-end" key={ojo}>
                              <div className="col-12 col-md-1"><strong>{ojo.toUpperCase()}</strong></div>
                              <div className="col-3 col-md"><label className="form-label small">Esfera</label><input className="form-control form-control-sm" value={form.refraccion[ojo].esf} onChange={e => setRefr(ojo, 'esf', e.target.value)} /></div>
                              <div className="col-3 col-md"><label className="form-label small">Cilindro</label><input className="form-control form-control-sm" value={form.refraccion[ojo].cil} onChange={e => setRefr(ojo, 'cil', e.target.value)} /></div>
                              <div className="col-3 col-md"><label className="form-label small">Eje</label><input className="form-control form-control-sm" value={form.refraccion[ojo].eje} onChange={e => setRefr(ojo, 'eje', e.target.value)} /></div>
                              <div className="col-3 col-md"><label className="form-label small">Adición</label><input className="form-control form-control-sm" value={form.refraccion[ojo].add} onChange={e => setRefr(ojo, 'add', e.target.value)} /></div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Aptitud visual: vigencia + lentes */}
                      {tipo === 'aptitud_visual' && (
                        <div className="row g-3 mb-3">
                          <div className="col-md-6">
                            <label className="form-label">Válido hasta</label>
                            <input type="date" className="form-control" value={form.vigencia_hasta} onChange={e => setField('vigencia_hasta', e.target.value)} />
                          </div>
                          <div className="col-md-6 d-flex align-items-end">
                            <div className="form-check">
                              <input className="form-check-input" type="checkbox" id="reqLentes" checked={form.requiere_lentes} onChange={e => setField('requiere_lentes', e.target.checked)} />
                              <label className="form-check-label" htmlFor="reqLentes">Requiere uso de lentes correctivos</label>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Diagnostico (medico / examen) */}
                      {(tipo === 'medico_oftalmologico' || tipo === 'examen_visual') && (
                        <div className="mb-3">
                          <label className="form-label">Diagnóstico</label>
                          <textarea className="form-control" rows={2} value={form.diagnostico} onChange={e => setField('diagnostico', e.target.value)} />
                        </div>
                      )}

                      <div className="mb-3">
                        <label className="form-label">Observaciones <small className="text-muted">(opcional)</small></label>
                        <textarea className="form-control" rows={2} value={form.observaciones} onChange={e => setField('observaciones', e.target.value)} />
                      </div>

                      <hr />
                      <div className="row g-3">
                        <div className="col-md-6">
                          <label className="form-label">Profesional (firma)</label>
                          <input className="form-control" value={form.profesional_nombre} onChange={e => setField('profesional_nombre', e.target.value)} />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">Registro profesional <small className="text-muted">(Senescyt / MSP)</small></label>
                          <input className="form-control" placeholder="N° de registro" value={form.profesional_registro} onChange={e => setField('profesional_registro', e.target.value)} />
                        </div>
                        <div className="col-md-4">
                          <label className="form-label">Lugar de emisión</label>
                          <input className="form-control" placeholder="Ciudad" value={form.lugar_emision} onChange={e => setField('lugar_emision', e.target.value)} />
                        </div>
                        <div className="col-md-4">
                          <label className="form-label">Fecha de emisión</label>
                          <input type="date" className="form-control" value={form.fecha_emision} onChange={e => setField('fecha_emision', e.target.value)} />
                        </div>
                        <div className="col-md-4">
                          <label className="form-label">Correo del paciente <small className="text-muted">(para enviar)</small></label>
                          <input type="email" className="form-control" value={form.email_destinatario} onChange={e => setField('email_destinatario', e.target.value)} />
                        </div>
                      </div>
                    </>
                  )
                )}
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={cerrarNuevo}>Cancelar</button>
                {tipo && paciente && !cargandoBorrador && (
                  <button className="btn btn-primary" onClick={emitir} disabled={emitiendo}>
                    {emitiendo ? <><span className="spinner-border spinner-border-sm me-1"></span>Emitiendo…</> : <><i className="bi bi-check-lg me-1"></i>Emitir certificado</>}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── MODAL PLANTILLAS ─── */}
      {showPlantillas && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-xl modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title"><i className="bi bi-pencil-square me-2"></i>Plantillas de certificados</h5>
                <button type="button" className="btn-close" onClick={() => setShowPlantillas(false)}></button>
              </div>
              <div className="modal-body">
                <p className="text-muted">
                  Edita el texto base de cada tipo. Las <strong>variables</strong> como <code>{'{{paciente}}'}</code> se
                  reemplazan solas con los datos del paciente al emitir. Lo que guardes aquí se usará en los próximos certificados.
                </p>

                <ul className="nav nav-pills mb-3 flex-wrap">
                  {TIPOS.map(t => (
                    <li className="nav-item" key={t.value}>
                      <button className={`nav-link ${tipoPlantilla === t.value ? 'active' : ''}`} onClick={() => setTipoPlantilla(t.value)}>
                        {t.label}
                      </button>
                    </li>
                  ))}
                </ul>

                {plantillaActual && (
                  <>
                    <div className="mb-2">
                      <label className="form-label">Título</label>
                      <input className="form-control" value={plantillaActual.titulo || ''} onChange={e => setPlantillaCampo('titulo', e.target.value)} />
                    </div>
                    <div className="mb-2">
                      <label className="form-label">Texto del certificado</label>
                      <textarea ref={cuerpoRef} className="form-control" rows={9} value={plantillaActual.cuerpo || ''} onChange={e => setPlantillaCampo('cuerpo', e.target.value)} />
                      <div className="mt-2">
                        <small className="text-muted d-block mb-1">Variables (clic para insertar donde está el cursor):</small>
                        <div className="d-flex flex-wrap gap-1">
                          {VARIABLES_UI.map(([k, d]) => (
                            <button key={k} type="button" className="btn btn-sm btn-light border font-monospace" title={d} onClick={() => insertarVariable(k)}>
                              {`{{${k}}}`}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="row g-2">
                      <div className="col-md-8">
                        <label className="form-label">Observaciones por defecto <small className="text-muted">(opcional)</small></label>
                        <input className="form-control" value={plantillaActual.observaciones || ''} onChange={e => setPlantillaCampo('observaciones', e.target.value)} />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">Vigencia por defecto (meses)</label>
                        <input type="number" min="0" className="form-control" value={plantillaActual.vigencia_meses ?? ''}
                          onChange={e => setPlantillaCampo('vigencia_meses', e.target.value === '' ? null : parseInt(e.target.value))} />
                      </div>
                    </div>
                  </>
                )}

                <hr />
                <h6 className="mb-2"><i className="bi bi-pen me-2"></i>Datos por defecto del emisor (firma)</h6>
                <p className="text-muted small mb-2">Se precargan en cada certificado para que solo tengas que emitir.</p>
                <div className="row g-2">
                  <div className="col-md-6">
                    <label className="form-label">Registro profesional</label>
                    <input className="form-control" placeholder="Senescyt / MSP" value={emisor.registro_profesional || ''}
                      onChange={e => setEmisor(em => ({ ...em, registro_profesional: e.target.value }))} />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Lugar de emisión</label>
                    <input className="form-control" placeholder="Ciudad" value={emisor.lugar_emision || ''}
                      onChange={e => setEmisor(em => ({ ...em, lugar_emision: e.target.value }))} />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowPlantillas(false)}>Cerrar</button>
                <button className="btn btn-primary" onClick={guardarPlantillas} disabled={guardandoPlantilla}>
                  {guardandoPlantilla ? <><span className="spinner-border spinner-border-sm me-1"></span>Guardando…</> : <><i className="bi bi-save me-1"></i>Guardar plantillas</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── MODAL ANULAR ─── */}
      {showAnular && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Anular certificado</h5>
                <button type="button" className="btn-close" onClick={() => setShowAnular(null)}></button>
              </div>
              <div className="modal-body">
                <p className="text-muted">El certificado quedará marcado como <strong>ANULADO</strong>. Indica el motivo:</p>
                <textarea className="form-control" rows={3} value={motivoAnular} onChange={e => setMotivoAnular(e.target.value)} placeholder="Motivo de la anulación (mínimo 5 caracteres)" />
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowAnular(null)}>Cancelar</button>
                <button className="btn btn-danger" onClick={confirmarAnular}>Anular</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Certificados;
